import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSEO } from '../utils/seo';
import { useCopy, useLocale } from '../i18n/LocaleContext';

const COPY = {
  fr: {
    seoTitle: 'Inscription — Le Placard à Doudous', seoDescription: 'Créez votre compte.', mismatch: 'Les mots de passe ne correspondent pas',
    tooShort: 'Mot de passe trop court (minimum 8 caractères)', created: 'Compte créé ! Bienvenue au Placard à Doudous !', error: 'Erreur lors de l’inscription',
    subtitle: 'Rejoignez la communauté !', name: 'Prénom & Nom', namePlaceholder: 'Margot Martin', email: 'Email', emailPlaceholder: 'votre@email.fr',
    password: 'Mot de passe', hidePassword: 'Masquer le mot de passe', showPassword: 'Afficher le mot de passe', confirm: 'Confirmer le mot de passe',
    loading: 'Création...', submit: 'Créer mon compte', hasAccount: 'Déjà un compte ?', login: 'Se connecter',
  },
  en: {
    seoTitle: 'Create an account — Le Placard à Doudous', seoDescription: 'Create your account.', mismatch: 'The passwords don’t match',
    tooShort: 'Password too short (8 characters minimum)', created: 'Account created! Welcome to Le Placard à Doudous!', error: 'Something went wrong while creating your account',
    subtitle: 'Join the community!', name: 'First & last name', namePlaceholder: 'Jane Smith', email: 'Email', emailPlaceholder: 'your@email.com',
    password: 'Password', hidePassword: 'Hide password', showPassword: 'Show password', confirm: 'Confirm password',
    loading: 'Creating...', submit: 'Create my account', hasAccount: 'Already have an account?', login: 'Log in',
  },
};

export default function Register() {
  const c = useCopy(COPY);
  const { locale, lp } = useLocale();
  useSEO(c.seoTitle, c.seoDescription);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error(c.mismatch); return; }
    if (form.password.length < 8) { toast.error(c.tooShort); return; }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password, locale);
      toast.success(c.created);
      navigate(lp('/compte'));
    } catch (err) {
      toast.error(err.message || c.error);
    }
    setLoading(false);
  };

  return (
    <div className="hero-mist noise min-h-screen flex items-center justify-center pt-20 pb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md mx-4"
      >
        <div className="rounded-3xl bg-card p-8 shadow-ambient sm:p-10">
          <div className="text-center mb-8">
            <Link to={lp('/')} className="font-serif text-2xl text-foreground">Le Placard à Doudous</Link>
            <p className="font-sans text-sm text-[hsl(var(--muted-foreground))] mt-2">{c.subtitle}</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="font-sans text-sm">{c.name}</Label>
              <Input id="name" autoComplete="name" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required className="rounded-xl font-sans" placeholder={c.namePlaceholder} data-testid="register-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="font-sans text-sm">{c.email}</Label>
              <Input id="email" type="email" autoComplete="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} required className="rounded-xl font-sans" placeholder={c.emailPlaceholder} data-testid="register-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-sans text-sm">{c.password}</Label>
              <div className="relative">
                <Input id="password" autoComplete="new-password" minLength={8} type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} required className="rounded-xl font-sans pr-10" placeholder="Minimum 8 caractères" data-testid="register-password-input" />
                <button type="button" aria-label={showPwd ? c.hidePassword : c.showPassword} aria-pressed={showPwd} onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm" className="font-sans text-sm">{c.confirm}</Label>
              <Input id="confirm" autoComplete="new-password" minLength={8} type="password" value={form.confirm} onChange={e => setForm(p => ({...p, confirm: e.target.value}))} required className="rounded-xl font-sans" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary text-primary-foreground font-sans font-medium shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-ambient)] transition-[box-shadow] duration-200" data-testid="register-submit-button">
              {loading ? c.loading : c.submit}
            </Button>
          </form>
          <p className="font-sans text-sm text-center text-[hsl(var(--muted-foreground))] mt-6">
            {c.hasAccount}{' '}
            <Link to={lp('/connexion')} className="text-[hsl(var(--primary))] font-medium hover:underline">{c.login}</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
